import {
  PartnerAccountBusinessCaseRequest,
  PartnerAccountCreationResponse,
  PartnerAccountRequest,
  dataTransformers,
  removeEmptyProperty,
  sanatizeURL,
} from 'vopay';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api.config';
import {
  sanitizeNotes,
  toDocumentDataUri,
} from '@/components/Accounts/AccountCreation/R1AccountCreation/useR1AccountSubmit';
import type { DetailsFormValues, SettingsFormValues } from './types';

export type PartnerAccountSubmitResult = {
  success: boolean;
  accountId: string;
  errorMessage: string;
  // Post-creation sub-requests that failed after the account itself was created.
  issues: string[];
  // Query string (status + balances) the account detail route needs, built from the fetched
  // account. Empty when the account wasn't created or couldn't be re-fetched.
  accountSearch: string;
};

function hasAccountId(res: unknown): res is PartnerAccountCreationResponse {
  return Object.prototype.hasOwnProperty.call(res, 'AccountID');
}

export default function usePartnerAccountSubmit() {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Issues' });
  const [creating, setCreating] = useState(false);

  async function submitPartnerAccount(
    details: DetailsFormValues,
    settings: SettingsFormValues,
  ): Promise<PartnerAccountSubmitResult> {
    try {
      setCreating(true);

      const accountName = details.dbaName.trim() || details.legalBusinessName.trim();
      const fullLegalName = `${details.firstName} ${details.lastName}`.trim();

      // Step 1: Create the account.
      const accountRes = await api.postPartnerAccount(
        removeEmptyProperty<PartnerAccountRequest>({
          Name: accountName,
          LegalBusinessName: details.legalBusinessName,
          BusinessName: accountName,
          EmailAddress: details.emailAddress,
          FullLegalName: fullLegalName,
          PrimaryContactFullName: fullLegalName,
          Country: details.country,
          OrganizationalType: details.businessType,
          BusinessTypeID: details.industry,
          BusinessTypeCategoryID: details.industrySubCategory,
          Address1: details.address,
          Address2: details.unit,
          City: details.city,
          Province: details.province,
          PostalCode: details.postalCode,
          WebsiteURL: details.websiteURL ? sanatizeURL(details.websiteURL) : '',
          BusinessPhone: details.businessPhone,
          ClientAccountsEnabled: settings.isClientAccountsEnabled,
          // Honoured explicitly by the route; when omitted the billing package drives it.
          SubAccountsEnabled: settings.isSubAccountsEnabled,
          // VoPay 360 isn't offered at creation.
          AccountingSoftwareIntegrationEnabled: false,
          BillingPackageID: settings.packageType,
          // The backend overrides this per partner type (Technology partners and VARs with
          // reporting liability never send the onboarding email).
          SendWelcomeEmail: true,
        }),
      );

      if (!accountRes.Success || !hasAccountId(accountRes)) {
        return {
          success: false,
          accountId: '',
          errorMessage: accountRes.ErrorMessage,
          issues: [],
          accountSearch: '',
        };
      }

      const accountId = accountRes.AccountID;
      const issues: string[] = [];

      // Runs a post-creation sub-request; on failure it records a message instead of throwing,
      // so a single failed step doesn't abort the rest (the account already exists).
      const capture = async <T extends { Success: boolean; ErrorMessage: string }>(
        label: string,
        request: Promise<T>,
      ): Promise<T | null> => {
        try {
          const res = await request;
          if (!res.Success)
            issues.push(res.ErrorMessage || t('requestFailed', { defaultValue: '{{label}} failed.', label }));

          return res;
        } catch {
          issues.push(t('requestFailed', { defaultValue: '{{label}} failed.', label }));

          return null;
        }
      };

      // Step 2: In parallel — fetch the new account (for its token and balances) and submit the
      // business case.
      const [accountsRes] = await Promise.all([
        capture(
          t('accountDetails', { defaultValue: 'Account details' }),
          api.fetchPartnerAccounts({ VopayAccountID: accountId, ShowToken: true }),
        ),
        capture(
          t('businessCase', { defaultValue: 'Business case' }),
          api.postPartnerAccountBusinessCase(
            removeEmptyProperty<PartnerAccountBusinessCaseRequest>({
              VopayAccountID: accountId,
              BusinessName: accountName,
              BusinessEmail: details.businessEmailAddress,
              BusinessPhone: details.businessPhone,
              BillingPackagesID: settings.packageType,
              WebsiteURL: details.websiteURL ? sanatizeURL(details.websiteURL) : '',
              BusinessModel: details.businessModel,
              NumberofYearsInBusiness: details.numberOfYears,
              MSB: details.isMSB,
              // The backend Notes validator rejects % \ / < > & outright - strip them rather than fail the call.
              Notes: sanitizeNotes(details.notes),
              PlatformUsage: settings.platformUsage,
              DataAggregator: settings.dataAggregator,
              SystemOfRecord: settings.accountSystem,
              SocialMediaPage: details.socialMediaPage ? sanatizeURL(details.socialMediaPage) : '',
              // The endpoint requires CompanyPhone alongside SocialMediaPage when the partner's
              // BusinessCaseRequired is set; the business phone is the only phone collected.
              CompanyPhone: details.businessPhone,
            }),
          ),
        ),
      ]);

      const { fromAccounts } = dataTransformers;
      const accounts = accountsRes ? fromAccounts(accountsRes.Accounts) : null;
      const token = accounts?.toAccountDetails[accountId]?.token ?? '';

      // Build the status + balances query string the account detail route expects.
      const createdBalance = accounts?.toBalances.find((balance) => balance.AccountID === accountId);

      const accountSearch = createdBalance
        ? new URLSearchParams({
            accountId: createdBalance.AccountID,
            isActive: String(createdBalance.IsActive),
            accountStatus: createdBalance.AccountStatus,
            currency: createdBalance.Currency,
            pending: createdBalance.PendingBalance,
            available: createdBalance.AvailableBalance,
            account: createdBalance.AccountBalance,
            reserve: createdBalance.Reserve,
            immediate: createdBalance.AvailableImmediately,
          }).toString()
        : '';

      // Step 3: Upload the flow-of-funds diagram (needs the new account's token). A missing token
      // is itself an upload failure — skipping silently would show a clean success for an account
      // missing its document.
      if (details.flowOfFundsFile) {
        const uploadLabel = t('flowOfFundsUpload', { defaultValue: 'Flow of funds upload' });

        if (token) {
          await capture(
            uploadLabel,
            api.postAccountDocumentUpload(
              {
                DocumentContent: toDocumentDataUri(details.flowOfFundsFile, details.flowOfFundsFileName),
                DocumentName: `Flow of Funds - ${accountName}`,
                DocumentType: 'flowoffunds',
              },
              token,
            ),
          );
        } else {
          issues.push(t('requestFailed', { defaultValue: '{{label}} failed.', label: uploadLabel }));
        }
      }

      return { success: true, accountId, errorMessage: '', issues, accountSearch };
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setCreating(false);
    }
  }

  return { creating, submitPartnerAccount };
}
